import {
  Button,
  CloseButton,
  RadioButton,
  TextArea,
  CustomSelect,
  Popup,
  SelectOption,
} from '@consigli/facade';
import { useSubmitFeedbackMutation } from '@consigli/hooks';
import { FeedbackType } from '@consigli/types';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoIosInformationCircle } from 'react-icons/io';
import { toast } from 'react-toastify';

import { useFeedbackServiceOptions } from './use-feedback-service-options';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FeedbackModal = ({ isOpen, onClose }: FeedbackModalProps) => {
  const { t } = useTranslation();
  const [submitFeedback, { isLoading }] = useSubmitFeedbackMutation();
  const [feedbackType, setFeedbackType] = useState<FeedbackType>(FeedbackType.GENERAL);
  const [selectedService, setSelectedService] = useState<SelectOption | null>(null);
  const [feedbackText, setFeedbackText] = useState('');

  const serviceOptions: SelectOption[] = useFeedbackServiceOptions();

  const canSubmit = useMemo(() => {
    if (!feedbackText.trim()) return false;
    if (feedbackType === FeedbackType.SKILL && !selectedService) return false;
    return true;
  }, [feedbackText, feedbackType, selectedService]);

  const resetForm = useCallback(() => {
    setFeedbackType(FeedbackType.GENERAL);
    setSelectedService(null);
    setFeedbackText('');
  }, []);

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      await submitFeedback({
        type: feedbackType,
        ...(feedbackType === FeedbackType.SKILL && selectedService
          ? { service: selectedService.value as string }
          : {}),
        message: feedbackText.trim(),
      }).unwrap();

      toast.success(t('feedback.success'));
      resetForm();
      onClose();
    } catch {
      toast.error(t('feedback.error'));
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Popup>
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-start">
          <h2 className="text-xl font-semibold">{t('feedback.title')}</h2>
          <CloseButton onClick={onClose} />
        </div>
        <p className="text-day-neutral-subtle">{t('feedback.description')}</p>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">{t('feedback.type-label')}</label>
          <div className="flex items-start gap-2 rounded-md bg-day-secondary p-3 text-sm text-day-neutral-subtle">
            <IoIosInformationCircle className="mt-0.5 shrink-0 text-day-neutral-subtle" size={16} />
            <span>{t('feedback.type-info')}</span>
          </div>
          <div className="flex gap-4">
            <RadioButton
              name="feedback-type"
              value={FeedbackType.GENERAL}
              label={t('feedback.general-label')}
              checked={feedbackType === FeedbackType.GENERAL}
              onChange={() => {
                setFeedbackType(FeedbackType.GENERAL);
                setSelectedService(null);
              }}
            />
            <RadioButton
              name="feedback-type"
              value={FeedbackType.SKILL}
              label={t('feedback.skill-label')}
              checked={feedbackType === FeedbackType.SKILL}
              onChange={() => setFeedbackType(FeedbackType.SKILL)}
            />
          </div>
        </div>

        {feedbackType === FeedbackType.SKILL && (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">{t('feedback.skill-select-label')}</label>
            <CustomSelect
              options={serviceOptions}
              value={selectedService ? [selectedService] : []}
              onChange={(options: SelectOption[]) => setSelectedService(options[0] || null)}
              placeholder={t('feedback.skill-placeholder')}
              className="w-full"
            />
          </div>
        )}

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">{t('feedback.text-label')}</label>
          <TextArea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder={t('feedback.text-placeholder')}
            rows={4}
            className="w-full"
          />
        </div>

        <div className="flex justify-end gap-2 mt-2">
          <Button primary rounded onClick={handleSubmit} disabled={isLoading || !canSubmit}>
            {isLoading ? t('feedback.submitting') : t('feedback.submit')}
          </Button>
        </div>
      </div>
    </Popup>
  );
};
